import { useState } from "react";
import {
    Calendar,
    Clock,
    MapPin,
    FileText,
    X,
    Edit,
    Trash2,
    Share2,
} from "lucide-react";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";
import { Link } from "react-router-dom";

export default function CompleteAppointmentSystem() {
    const [showModal, setShowModal] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);

    // Sample appointment data
    const appointments = {
        upcoming: [
            {
                id: 1,
                date: "9 Feb 2024",
                timeSlot: "09:00 AM - 09:30 AM",
                doctorName: "Sandeep Singh",
                specialty: "Cardiology",
                status: "Confirmed",
                location: "Cardiology Department, Floor 3, Room 302",
                notes: "Follow-up appointment for routine heart checkup. Please bring previous test reports.",
                patientInstructions:
                    "Please arrive 15 minutes before appointment. Fasting not required.",
            },
            {
                id: 2,
                date: "10 Feb 2024",
                timeSlot: "09:30 AM - 10:30 AM",
                doctorName: "Sandeep Singh",
                specialty: "Cardiology",
                status: "Rescheduled",
                location: "Cardiology Department, Floor 3, Room 302",
                notes: "Rescheduled from previous date. Follow-up appointment for routine heart checkup.",
                patientInstructions:
                    "Please arrive 15 minutes before appointment. Fasting not required.",
            },
        ],
        past: [
            {
                id: 4,
                date: "9 Jan 2024",
                timeSlot: "09:00 AM - 09:30 AM",
                doctorName: "Sandeep Singh",
                specialty: "Cardiology",
                status: "Complete",
                location: "Cardiology Department, Floor 3, Room 302",
                notes: "Follow-up appointment for routine heart checkup. Please bring previous test reports.",
                patientInstructions:
                    "Please arrive 15 minutes before appointment. Fasting not required.",
            },
            {
                id: 5,
                date: "12 Dec 2023",
                timeSlot: "09:00 AM - 09:30 AM",
                doctorName: "Sandeep Singh",
                specialty: "Cardiology",
                status: "Cancelled",
                location: "Cardiology Department, Floor 3, Room 302",
                notes: "Cancelled due to doctor's emergency.",
                patientInstructions: "N/A",
            },
            {
                id: 6,
                date: "12 Dec 2023",
                timeSlot: "09:00 AM - 09:30 AM",
                doctorName: "Sandeep Singh",
                specialty: "Cardiology",
                status: "Complete",
                location: "Cardiology Department, Floor 3, Room 302",
                notes: "Follow-up appointment completed. Medications prescribed.",
                patientInstructions: "Take medications as prescribed.",
            },
            {
                id: 7,
                date: "12 Dec 2023",
                timeSlot: "09:00 AM - 09:30 AM",
                doctorName: "Sandeep Singh",
                specialty: "Cardiology",
                status: "Complete",
                location: "Cardiology Department, Floor 3, Room 302",
                notes: "Follow-up appointment completed. Medications prescribed.",
                patientInstructions: "Take medications as prescribed.",
            },
        ],
    };

    const handleAppointmentClick = (appointment) => {
        setSelectedAppointment(appointment);
        setShowModal(true);
    };

    return (
        <>
        <Navbar />
        <div className="max-w-4xl px-4 mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center mb-4 text-xs text-gray-500">
                <span className="cursor-pointer hover:underline">Home</span>
                <span className="mx-1">/</span>
                <span className="cursor-pointer hover:underline">
                    Patient Portal
                </span>
                <span className="mx-1">/</span>
                <span className="cursor-pointer hover:underline">
                    My Wellness Space
                </span>
                <span className="mx-1">/</span>
                <span className="text-gray-700">Appointment</span>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                    <button className="mr-2 text-gray-700">
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M19 12H5M5 12L12 19M5 12L12 5"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                    <h1 className="text-lg font-medium text-gray-800">
                        Appointment
                    </h1>
                </div>
                <Link to="/book-appointment">
                <button className="flex items-center px-3 py-2 text-sm text-white bg-teal-500 rounded-md">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4 mr-1"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fillRule="evenodd"
                            d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                            clipRule="evenodd"
                        />
                    </svg>
                    New Appointment
                </button>
                </Link>
            </div>

            {/* Upcoming Appointments */}
            <div className="mb-6">
                <div className="flex items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">
                        Upcoming
                    </span>
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                        2
                    </span>
                </div>

                <div className="overflow-hidden border border-gray-200 rounded-md">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-300">
                            <tr>
                                <th
                                    scope="col"
                                    className="px-6 py-2 text-xs font-medium tracking-wider text-left text-gray-700 uppercase"
                                >
                                    Appointment Date
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-2 text-xs font-medium tracking-wider text-left text-gray-700 uppercase"
                                >
                                    Time Slot
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-2 text-xs font-medium tracking-wider text-left text-gray-700 uppercase"
                                >
                                    Doctor Name
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-2 text-xs font-medium tracking-wider text-left text-gray-700 uppercase"
                                >
                                    Specialty
                                </th>
                                <th scope="col" className="relative px-6 py-2">
                                    <span className="sr-only">Actions</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {appointments.upcoming.map((appointment, index) => (
                                <tr
                                    key={index}
                                    className="cursor-pointer hover:bg-gray-50"
                                    onClick={() =>
                                        handleAppointmentClick(appointment)
                                    }
                                >
                                    <td className="px-6 py-3 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {appointment.date}
                                            {appointment.status ===
                                                "Rescheduled" && (
                                                <span className="text-xs text-gray-500">
                                                    {" "}
                                                    (Rescheduled)
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {appointment.timeSlot}
                                        </div>
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {appointment.doctorName}
                                        </div>
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {appointment.specialty}
                                        </div>
                                    </td>
                                    <td className="px-6 py-3 text-right whitespace-nowrap">
                                        <div className="text-sm text-blue-600">
                                            •••
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            <tr className="hover:bg-gray-50">
                                <td className="px-6 py-3 whitespace-nowrap">
                                    <div className="text-sm text-gray-400 line-through">
                                        12 Feb 2024
                                    </div>
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap">
                                    <div className="text-sm text-gray-400 line-through">
                                        09:00 AM - 09:30 AM
                                    </div>
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap">
                                    <div className="text-sm text-gray-400 line-through"></div>
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap">
                                    <div className="text-sm text-gray-400 line-through"></div>
                                </td>
                                <td className="px-6 py-3 text-right whitespace-nowrap"></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Past Appointments */}
            <div>
                <div className="flex items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">
                        Past Appointments
                    </span>
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                        4
                    </span>
                </div>

                <div className="overflow-hidden border border-gray-200 rounded-md">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-300">
                            <tr>
                                <th
                                    scope="col"
                                    className="px-6 py-2 text-xs font-medium tracking-wider text-left text-gray-700 uppercase"
                                >
                                    Appointment Date
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-2 text-xs font-medium tracking-wider text-left text-gray-700 uppercase"
                                >
                                    Time Slot
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-2 text-xs font-medium tracking-wider text-left text-gray-700 uppercase"
                                >
                                    Doctor Name
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-2 text-xs font-medium tracking-wider text-left text-gray-700 uppercase"
                                >
                                    Specialty
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-2 text-xs font-medium tracking-wider text-left text-gray-700 uppercase"
                                >
                                    Status
                                </th>
                                <th scope="col" className="relative px-6 py-2">
                                    <span className="sr-only">Actions</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {appointments.past.map((appointment, index) => (
                                <tr
                                    key={index}
                                    className="cursor-pointer hover:bg-gray-50"
                                    onClick={() =>
                                        handleAppointmentClick(appointment)
                                    }
                                >
                                    <td className="px-6 py-3 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {appointment.date}
                                        </div>
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {appointment.timeSlot}
                                        </div>
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {appointment.doctorName}
                                        </div>
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {appointment.specialty}
                                        </div>
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap">
                                        <div
                                            className={`text-sm ${
                                                appointment.status ===
                                                "Complete"
                                                    ? "text-green-500"
                                                    : appointment.status ===
                                                      "Cancelled"
                                                    ? "text-red-500"
                                                    : "text-gray-900"
                                            }`}
                                        >
                                            {appointment.status}
                                        </div>
                                    </td>
                                    <td className="px-6 py-3 text-right whitespace-nowrap">
                                        <div className="text-sm text-blue-600">
                                            •••
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Clickable appointment text */}
            <div className="mt-4 text-center">
                <span
                    className="text-xs font-medium text-center text-gray-700 cursor-pointer"
                    onClick={() =>
                        handleAppointmentClick(appointments.upcoming[0])
                    }
                >
                    Appointment
                </span>
            </div>

            {/* Modal */}
            {showModal && selectedAppointment && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="w-full max-w-md p-6 mx-4 bg-white rounded-lg shadow-lg">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-gray-800">
                                Appointment Details
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="p-1 text-gray-500 transition-colors rounded-full hover:bg-gray-100"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Doctor info */}
                        <div className="flex items-center p-3 mb-4 rounded-lg bg-gray-50">
                            <img
                                src="/api/placeholder/80/80"
                                alt={selectedAppointment.doctorName}
                                className="w-16 h-16 mr-4 rounded-full"
                            />
                            <div>
                                <h3 className="font-semibold text-gray-800">
                                    Dr. {selectedAppointment.doctorName}
                                </h3>
                                <p className="text-sm text-gray-600">
                                    {selectedAppointment.specialty}
                                </p>
                            </div>
                        </div>

                        {/* Appointment details */}
                        <div className="mb-4 space-y-3">
                            <div className="flex items-start">
                                <Calendar className="w-5 h-5 mt-0.5 mr-3 text-teal-600" />
                                <div>
                                    <p className="text-sm font-medium text-gray-900">
                                        Date
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        {selectedAppointment.date}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start">
                                <Clock className="w-5 h-5 mt-0.5 mr-3 text-teal-600" />
                                <div>
                                    <p className="text-sm font-medium text-gray-900">
                                        Time
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        {selectedAppointment.timeSlot}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start">
                                <MapPin className="w-5 h-5 mt-0.5 mr-3 text-teal-600" />
                                <div>
                                    <p className="text-sm font-medium text-gray-900">
                                        Location
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        {selectedAppointment.location}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start">
                                <FileText className="w-5 h-5 mt-0.5 mr-3 text-teal-600" />
                                <div>
                                    <p className="text-sm font-medium text-gray-900">
                                        Notes
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        {selectedAppointment.notes}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start">
                                <FileText className="w-5 h-5 mt-0.5 mr-3 text-teal-600" />
                                <div>
                                    <p className="text-sm font-medium text-gray-900">
                                        Patient Instructions
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        {
                                            selectedAppointment.patientInstructions
                                        }
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex justify-end space-x-2">
                            <button className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 border rounded-md hover:bg-gray-100">
                                <Edit size={16} className="mr-1" />
                                Edit
                            </button>
                            <button className="flex items-center px-3 py-1.5 text-sm font-medium text-red-600 border rounded-md hover:bg-red-100">
                                <Trash2 size={16} className="mr-1" />
                                Cancel
                            </button>
                            <button className="flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 border rounded-md hover:bg-blue-100">
                                <Share2 size={16} className="mr-1" />
                                Share
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
        <Footer />
        </>
    );
}
